import React, { useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { ChevronLeft, Search, MoreVertical, Plus } from "lucide-react";
import { SortingState } from "@tanstack/react-table";

import AttachedFeeSidepanel from "./sidebars/AttachedFeeSidepanel";
import PaymentModal from "./modals/PaymentModal";
import PaymentSuccessModal from "./modals/PaymentSuccessModal";
import PaymentReceiptModal from "./modals/PaymentReceiptModal";
import { CLOSED_EYE } from "../../../../assets";
import SuccessModalV2 from "../../../../shared/components/successModal/sucessModalV2";
import SubmitTransferModal from "./modals/SubmitTransferModal";
import SetupMeetingModal from "./modals/SetupMeetingModal";
import TableComponent from "../../../../shared/components/table";
import { PaymentHistoryColumns } from "../../../../shared/components/table/columns/paymentHistory";

const DOCUMENTS = [
  {
    id: 1,
    name: "companies_demo_export.xlsx",
    date: "2021-11-04 11:54",
    type: "xlsx",
    color: "bg-[#00B074]", // Green
  },
  {
    id: 2,
    name: "demo_image.jpg",
    date: "2021-11-03 22:00",
    type: "jpg",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 3,
    name: "sample_demo_export.xlsx",
    date: "2021-11-02 11:09",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 4,
    name: "visit_demo_export.xlsx",
    date: "2021-10-31 17:24",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
];

const PAYMENT_DATA = [
  {
    sn: false,
    date: "31-05-2024",
    amount: "₦2,235.00",
    status: "Paid",
    description: "Olugbenga Akintola vs Israel Anyemalu",
    payment: "VIEW Receipt",
  },
  {
    sn: false,
    date: "31-05-2024",
    amount: "₦3,635.00",
    status: "Paid",
    description: "Olugbenga Akintola vs Israel Anyemalu",
    payment: "VIEW Receipt",
  },
  {
    sn: false,
    date: "31-05-2024",
    amount: "₦2,235.00",
    status: "Paid",
    description: "Olugbenga Akintola vs Israel Anyemalu",
    payment: "VIEW Receipt",
  },
];

const ViewCaseFolder = () => {
  const navigate = useNavigate();

  const [serviceSuccessModal, setServiceSuccessModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingSuccessModal, setMeetingSuccessModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isFeeSidepanelOpen, setIsFeeSidepanelOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
    useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [showEndorsementModal, setShowEndorsementModal] = useState(false);
  const [endorsementText, setEndorsementText] = useState("");
  const [viewMode, setViewMode] = useState<"documents" | "payments">(
    "documents",
  );
  const [paymentSearchTerm, setPaymentSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);

  const handlePay = () => {
    setIsPaymentModalOpen(false);
    setIsPaymentSuccessModalOpen(true);
  };

  const handleViewReceipt = () => {
    setIsPaymentSuccessModalOpen(false);
    setIsReceiptModalOpen(true);
  };

  const handleOpenReceiptFromTable = (_data: any) => {
    setIsReceiptModalOpen(true);
  };

  const filteredPayments = PAYMENT_DATA.filter((payment) => {
    if (!paymentSearchTerm.trim()) return true;
    const term = paymentSearchTerm.toLowerCase();
    return (
      payment.date.toLowerCase().includes(term) ||
      payment.amount.toLowerCase().includes(term) ||
      payment.status.toLowerCase().includes(term) ||
      payment.description.toLowerCase().includes(term)
    );
  });

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto  min-h-screen">
      <AttachedFeeSidepanel
        isOpen={isFeeSidepanelOpen}
        onClose={() => setIsFeeSidepanelOpen(false)}
        onPay={() => {
          setIsFeeSidepanelOpen(false);
          setIsPaymentModalOpen(true);
        }}
      />
      <PaymentModal
        open={isPaymentModalOpen}
        close={() => setIsPaymentModalOpen(false)}
        onPay={handlePay}
      />
      <PaymentSuccessModal
        open={isPaymentSuccessModalOpen}
        close={() => setIsPaymentSuccessModalOpen(false)}
        onViewReceipt={handleViewReceipt}
      />
      <PaymentReceiptModal
        open={isReceiptModalOpen}
        close={() => setIsReceiptModalOpen(false)}
      />
      {/* Header & Breadcrumbs */}
      <div className="bg-white border-b border-[#E9EAEB] px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-x-2 text-sm text-[#475467] mb-6">
          <button
            onClick={() => navigate("/lawyer/case-folders")}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span
            onClick={() => navigate("/lawyer/case-folders")}
            className="cursor-pointer hover:text-[#1A1A1A] transition-colors"
          >
            Case folders
          </span>
          <span className="text-[#D0D5DD]">{">"}</span>
          {viewMode === "payments" ? (
            <>
              <span
                onClick={() => setViewMode("documents")}
                className="cursor-pointer text-[#667085] hover:text-[#1A1A1A] transition-colors"
              >
                Olugbenga Akintola vs Israel Anyamelu
              </span>
              <span className="text-[#D0D5DD]">{">"}</span>
              <span className="font-medium text-[#1A1A1A]">View Payments</span>
            </>
          ) : (
            <span className="font-medium text-[#1A1A1A]">
              Olugbenga Akintola vs Israel Anyamelu
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">
            {viewMode === "payments"
              ? "Payments"
              : "Olugbenga Akintola vs Israel Anyamelu"}
          </h1>
          <div className="flex items-center gap-x-4">
            <button className="px-6 py-2 rounded-full border border-[#00B074] text-[#00B074] text-sm font-semibold bg-white hover:bg-[#ECFDF3] transition-colors">
              Schedule Hearing
            </button>
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00B074] text-white hover:bg-[#009060] transition-colors">
                <Plus className="w-5 h-5" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                  <div className="px-1 py-1 flex flex-col gap-y-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setShowEndorsementModal(true)}
                          className={`${
                            active
                              ? "bg-[#E6F7F1] text-[#027A48]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium text-left`}
                        >
                          Endorsement
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm text-left`}
                        >
                          Case Note
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm text-left`}
                        >
                          Upload Documents
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm text-left`}
                          onClick={() => setViewMode("payments")}
                        >
                          View Payment
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 flex flex-col gap-y-8">
        {viewMode === "documents" ? (
          <>
            <div className="w-full max-w-[264px] relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm placeholder:text-[#667085] focus:outline-none focus:border-[#00B074] focus:ring-1 focus:ring-[#00B074] shadow-sm"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]" />
            </div>

            <div className="flex flex-col gap-y-3">
              {DOCUMENTS.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-white border border-[#E9EAEB] rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center gap-x-4 flex-1">
                    <div
                      className={`w-10 h-10 rounded-full ${doc.color} flex items-center justify-center text-white font-medium text-sm`}
                    >
                      {doc.name.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#101828] truncate">
                        {doc.name}
                      </span>
                    </div>

                    <div className="text-sm text-[#475467] w-40">
                      {doc.date}
                    </div>
                  </div>

                  <div className="flex items-center gap-x-4">
                    <button className="flex items-center gap-x-2 text-[#027A48] hover:text-[#026aa2] px-3 py-2 rounded-lg transition-colors">
                      <img
                        src={CLOSED_EYE}
                        className="w-[24px] h-[24px]"
                        alt=""
                      />
                      <span className="text-sm font-medium">View</span>
                    </button>

                    <button className="flex items-center gap-x-2 bg-[#ECFDF3] text-[#027A48] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#d1fadf] transition-colors">
                      Download
                    </button>

                    {/* <button
                      onClick={() => {}}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FEF3F2] text-[#B42318] hover:bg-[#fee4e2] transition-colors"
                    >
                      <img src={RED_BIN} className="w-[11px] h-[11px]" alt="" />
                    </button> */}

                    <Menu as="div" className="relative inline-block text-left">
                      <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
                        <MoreVertical className="w-5 h-5" />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                          <div className="px-1 py-1 flex flex-col gap-y-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={`${
                                    active ? "bg-gray-50" : ""
                                  } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                                >
                                  View
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={`${
                                    active ? "bg-gray-50" : ""
                                  } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                                >
                                  Details
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => setServiceSuccessModal(true)}
                                  className={`${
                                    active
                                      ? "bg-[#E6F7F1] text-[#00B074]"
                                      : "text-[#344054]"
                                  } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
                                >
                                  Mark for service
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={`${
                                    active
                                      ? "bg-[#E6F7F1] text-[#00B074]"
                                      : "text-[#344054]"
                                  } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
                                >
                                  Sign and stamp
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-y-6">
            <div className="relative w-full max-w-[300px]">
              <input
                type="text"
                placeholder="Search"
                value={paymentSearchTerm}
                onChange={(e) => setPaymentSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] focus:ring-1 focus:ring-[#00B074] bg-white text-sm placeholder:text-[#667085] shadow-sm"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]" />
            </div>

            <div className="bg-white overflow-hidden rounded-xl">
              <TableComponent
                DATA={filteredPayments}
                // @ts-ignore
                COLUMNS={PaymentHistoryColumns(handleOpenReceiptFromTable)}
                sorting={sorting}
                setSorting={setSorting}
              />

              <div className="px-6 py-4 border-t border-[#E9EAEB] flex items-center justify-between">
                <div className="hidden sm:block"></div>

                <div className="flex items-center gap-x-6 w-full sm:w-auto justify-between sm:justify-end">
                  <span className="text-sm text-[#344054] font-medium">
                    Page 1 of 10
                  </span>

                  <div className="flex items-center gap-x-3">
                    <button className="px-3 py-2 border border-[#D0D5DD] rounded-lg text-sm font-medium text-[#344054] hover:bg-gray-50 transition-colors bg-white shadow-sm">
                      Previous
                    </button>
                    <button className="px-3 py-2 border border-[#D0D5DD] rounded-lg text-sm font-medium text-[#344054] hover:bg-gray-50 transition-colors bg-white shadow-sm">
                      Next
                    </button>
                  </div>

                  <div className="flex items-center gap-x-2 text-sm text-[#344054]">
                    <span>Entries per page:</span>
                    <select className="border border-[#D0D5DD] rounded-lg p-1.5 focus:outline-none focus:border-[#00B074] bg-white font-medium">
                      <option>10</option>
                      <option>20</option>
                      <option>50</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <SuccessModalV2
        open={serviceSuccessModal}
        close={() => setServiceSuccessModal(false)}
        message={"Submitted for service Successfully"}
      />
      <SubmitTransferModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onClick={() => {
          setShowSubmitModal(false);
          setServiceSuccessModal(true);
        }}
      />
      <SetupMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        onSubmit={() => {
          setShowMeetingModal(false);
          setMeetingSuccessModal(true);
        }}
      />
      <SuccessModalV2
        open={meetingSuccessModal}
        close={() => setMeetingSuccessModal(false)}
        message="Meeting setup successfully "
      />
      {showEndorsementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl px-10 py-8 relative">
            <button
              onClick={() => setShowEndorsementModal(false)}
              className="absolute top-6 right-6 text-[#22C55E] text-xl"
            >
              ×
            </button>
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#22C55E] flex items-center justify-center text-white font-semibold">
                  H
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-base font-semibold text-[#111827]">
                    Henry Alan
                  </span>
                  <p className="text-sm text-[#4B5563] max-w-xl">
                    Understanding the judicial consequence of the act under
                    section 54 of the amended constitution of the Federal
                    Republic of Nigeria.
                  </p>
                </div>
              </div>
              <span className="text-sm text-[#9CA3AF] font-medium">
                03/5/2021
              </span>
            </div>
            <hr className="border-[#E5E7EB] mb-6" />
            <div className="flex items-center gap-2 mb-4">
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                =
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                ≡
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                ••
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                🔗
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                T
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] font-bold text-sm">
                B
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F9FAFB] text-[#111827] text-sm">
                U
              </button>
              <div className="ml-auto">
                <select className="border border-[#E5E7EB] rounded-md px-3 py-1 text-xs text-[#4B5563] bg-white">
                  <option>Switzer Regular</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 border border-[#D1D5DB] rounded-lg px-4 py-2 mt-2">
              <div className="w-9 h-9 rounded-full bg-[#22C55E] flex items-center justify-center text-white font-semibold">
                H
              </div>
              <input
                type="text"
                placeholder="Add endorsement"
                value={endorsementText}
                onChange={(e) => setEndorsementText(e.target.value)}
                className="flex-1 border-none outline-none text-sm text-[#111827] placeholder:text-[#9CA3AF]"
              />
              <button
                onClick={() => {
                  if (endorsementText.trim()) {
                    setEndorsementText("");
                  }
                }}
                className="w-9 h-9 flex items-center justify-center rounded-md bg-[#22C55E] text-white text-sm"
              >
                ↑
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCaseFolder;
